import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuotes, useBulkDeleteQuotes, useBulkUpdateQuotes, useUsers } from '@/hooks/use-api'
import { ownerPickerOptions } from '@/lib/salesReps'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { FileText, Plus, DollarSign, CheckCircle, Download } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { exportQuotesToCSV } from '@/lib/csv-export'
import type { Quote as _Quote } from '@/types'
import { CreateQuoteModal } from '@/components/CreateQuoteModal'
import { MobileQuoteList } from '@/components/MobileQuoteCard'
import { useMobile } from '@/hooks/use-mobile'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { createQuotesColumns, quotesDefaultColumnSizing } from '@/config/columns/quotes-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { explainError } from '@/lib/explain-error'

export default function Quotes() {
  const navigate = useNavigate()
  const { currentUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'quotes')
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'created_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'desc')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)

  const isMobile = useMobile()
  const { isAdmin, impersonatedUser } = useUser()

  // Only Admin/Super Admin can export / bulk-reassign owner (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const canReassignOwner = canExport

  const { data, isLoading } = useQuotes({ search, page, sort_by: sortBy, sort_order: sortOrder })

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )
  const bulkDeleteMutation = useBulkDeleteQuotes()
  const bulkUpdateMutation = useBulkUpdateQuotes()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const { toast } = useToast()

  const {
    items: quotes,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
    stats,
  } = extract(data)

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of quotes
    // In a production app, you might want to fetch all quotes from the API
    exportQuotesToCSV(quotes)
    toast({
      title: 'Export Successful',
      description: `Exported ${quotes.length} quotes to CSV`,
    })
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === quotes.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(quotes.map((quote) => quote.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} quote${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete quotes')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} quote${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update quotes')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Define field configurations
  const quotesUpdateFields: BulkUpdateField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'Sent', label: 'Sent' },
        { value: 'Accepted', label: 'Accepted' },
        { value: 'Rejected', label: 'Rejected' },
      ],
    },
    ...(canReassignOwner
      ? [
          {
            key: 'owner_id',
            label: 'Owner',
            type: 'select' as const,
            options: ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name })),
          },
        ]
      : []),
  ]

  // Create columns with callbacks
  const columns = useMemo(
    () =>
      createQuotesColumns({
        onViewQuote: (quoteId: string, _name: string) => {
          navigate(`/quotes/${quoteId}`)
        },
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === quotes.length && quotes.length > 0,
      }),
    [navigate, selectedIds, quotes.length]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility('quotes-column-visibility')

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="quote"
          onClearSelection={handleClearSelection}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="quote" />
            <h1 className="text-3xl font-bold tracking-tight">Quotes</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage your sales quotes and proposals</p>
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['quotes']} label="quotes" />
          {canExport && (
            <Button variant="outline" aria-label="Export quotes to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button aria-label="Create new quote" onClick={() => setCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Quote
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Quotes</CardTitle>
            <FileText className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.totalQuotes ?? 0}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {quotes.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.totalValue ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.accepted ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Accepted Value</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.acceptedValue ?? 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Quotes</CardTitle>
          <CardDescription>View and manage your quotes</CardDescription>
          <div className="flex items-center gap-2">
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search quotes..."
              ariaLabel="Search quotes"
              entityKey="quotes"
            />
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading quotes...</div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No quotes found. Create your first quote to get started.
            </div>
          ) : isMobile ? (
            <MobileQuoteList
              quotes={quotes}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
          ) : (
            <ResizableTable
              columns={columns}
              data={quotes}
              storageKey="quotes-column-widths"
              defaultColumnSizing={quotesDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} quotes
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateQuoteModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="quote"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="quote"
        fields={quotesUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />
    </div>
  )
}
